<template>

    <div class="flex justify-between space-x-16">
        <div class="flex flex-col w-[50%] space-y-2">

            <div class="flex flex-col">
                <Label>{{ $t('quote.fields.quote_to')}}</Label>

                <template v-if="customer">

                    <div class="bg-gray-200 p-4 rounded-md">
                        <template v-if="customer.billing_address?.firstName && customer.billing_address?.lastName">
                            {{ customer.billing_address?.firstName }} {{ customer.billing_address?.lastName }}<br/>
                        </template>

                        <template v-if="customer.billing_address?.company">
                            {{ customer.billing_address?.company }}<br/>
                        </template>

                        <template v-if="customer.billing_address?.address">
                            {{ customer.billing_address?.address }}<br/>
                        </template>

                        <template v-if="customer.billing_address?.address2">
                            {{ customer.billing_address?.address2 }}<br/>
                        </template>

                        <template v-if="customer.billing_address?.city">
                        {{ customer.billing_address?.city }}<br/>
                        </template>

                        <template v-if="customer.billing_address?.zip">
                            {{ customer.billing_address?.zip }}<br/>
                        </template>

                        <template v-if="customer.billing_address?.country">
                            {{ customer.billing_address?.country }}<br/>
                        </template>

                        <template v-if="customer.billing_address?.email">
                            {{ customer.billing_address?.email }}<br/>
                        </template>
                    </div>

                </template>

                <CustomerSelector
                    v-else
                    :buttonLabel="$t('quote.select_customer')"
                    @selected="(selectedCustomer) => $emit('customerSelected', selectedCustomer)"
                ></CustomerSelector>

            </div>

            <div class="flex flex-col">
                <Label>{{ $t('quote.fields.project_name')}}</Label>
                <Text v-model="form.data.project_name" @change="$emit('changed')"></Text>
            </div>

            <div class="flex flex-col">
                <Label>{{ $t('quote.fields.logotype')}}</Label>
                <Text v-model="form.data.logotype" @change="$emit('changed')"></Text>
            </div>

            <div class="flex flex-col">
                <Label>{{ $t('quote.fields.header_text')}}</Label>
                <TextArea :rows="2" v-model="form.data.header_text" @change="$emit('changed')"></TextArea>
            </div>

            <div class="flex">
                <div class="flex items-center space-x-2">
                    <Label>{{ $t('quote.fields.signature')}}:</Label>
                    <div>{{ admin?.meta?.signature }}</div>
                </div>
            </div>
        </div>

        <div class="flex flex-col w-[50%] space-y-2">

            <div class="flex space-x-2 items-center w-full justify-end">
                <Button @click="$emit('new')">{{ $t('quote.new_quote') }}</Button>
                <Button @click="$emit('save')">{{ $t('quote.save_quote') }}</Button>
                <Button @click="$emit('close')">{{ $t('quote.bar.close') }}</Button>
            </div>

            <div class="flex flex-col">
                <Label>{{ $t('quote.fields.quote_number')}}</Label>
                <Text :readonly="true" :model-value="form.data.number"></Text>
            </div>

            <div v-if="customer" class="flex flex-col">
                <Label>{{ $t('quote.shipping_address')}}</Label>
                <select class="cm-select-input" v-model="form.data.shipping_address_id" @change="$emit('changed')">
                    <option :value="null">-- Select --</option>
                    <template v-for="shippingAddress in customer.shipping_addresses" :key="shippingAddress.id">
                        <option :value="shippingAddress.id">
                            {{ shippingAddress.firstName }} {{ shippingAddress.lastName }}, {{ shippingAddress.city }}, {{ shippingAddress.zip }}, {{ shippingAddress.country }}
                        </option>
                    </template>
                </select>

                <template v-if="quotePreviewError">
                    <div class="cm-form-error-message">{{ $t(quotePreviewError) }}</div>
                </template>
            </div>

            <div class="flex flex-col">
                <Label>{{ $t('quote.fields.delivery_date')}}</Label>
                <Text v-model="form.data.delivery_date" @change="$emit('changed')"></Text>
            </div>

            <div class="flex flex-col">
                <Label>{{ $t('quote.fields.delivery_terms')}}</Label>
                <Text v-model="form.data.delivery_terms" @change="$emit('changed')"></Text>
            </div>

            <div class="flex flex-col">
                <Label>{{ $t('quote.fields.terms_of_payment')}}</Label>
                <Text v-model="form.data.terms_of_payment" @change="$emit('changed')"></Text>
            </div>

            <div class="flex flex-col">
                <Label>{{ $t('quote.fields.currency')}}</Label>

                <select class="cm-select-input" v-model="form.data.currency" @change="$emit('changed')">
                    <option v-for="(currencyData, currencyCode) in currencies" :value="currencyCode">{{ currencyCode }}</option>
                </select>

            </div>
        </div>
    </div>

    <Table class="mt-4">

        <template #colgroup>
            <col class="w-[5%]" />
            <col class="w-[20%]" />
            <col class="w-[20%]" />
            <col class="w-[5%]" />
            <col class="w-[8%]" />
            <col class="w-[8%]" />
            <col class="w-[8%]" />
            <col class="w-[10%]" />
            <col class="w-[10%]" />
        </template>

        <template #head>
            <TableHead>{{ $t('quote.items_table.headers.show_in_pdf') }}</TableHead>
            <TableHead>{{ $t('quote.items_table.headers.name') }}</TableHead>
            <TableHead>{{ $t('quote.items_table.headers.description') }}</TableHead>
            <TableHead>{{ $t('quote.items_table.headers.quantity') }}</TableHead>
            <TableHead>{{ $t('quote.items_table.headers.unit_price') }}</TableHead>
            <TableHead>
                <div class="flex items-center space-x-2">
                    <div>{{ $t('quote.items_table.headers.discount') }}</div>
                    <div>
                        <input v-model="form.data.show_discount" class="cm-input__checkbox" type="checkbox" @change="$emit('changed')" />
                    </div>
                </div>

            </TableHead>
            <TableHead>{{ $t('quote.items_table.headers.tax') }}</TableHead>
            <TableHead>{{ $t('quote.items_table.headers.total') }}</TableHead>
            <TableHead>{{ $t('quote.items_table.headers.actions') }}</TableHead>
        </template>

        <template #body>

            <template v-for="item in form.items">

                <TableRow>

                    <TableColumn class="w-[10%]">
                        <input v-model="item.show_in_pdf" class="cm-input__checkbox" type="checkbox" @change="$emit('changed')" />
                    </TableColumn>

                    <TableColumn class="w-[20%]">
                        <Text v-model="item.number" @change="$emit('changed')"></Text>
                    </TableColumn>

                    <TableColumn class="w-[20%]">
                        <Text v-model="item.description" @change="$emit('changed')"></Text>
                    </TableColumn>

                    <TableColumn class="w-[5%]">
                        <Numeric v-model="item.quantity" :unsigned="true" :decimals="0" @change="$emit('changed')"></Numeric>
                    </TableColumn>

                    <TableColumn class="w-[8%]">
                        <Numeric
                            v-model="item.unit_price"
                            :unsigned="true"
                            :decimals="2"
                            decimal-separator=","
                            @change="$emit('changed')"
                        ></Numeric>
                    </TableColumn>

                    <TableColumn class="w-[8%]">
                        <Numeric
                            v-model="item.discount"
                            :unsigned="true"
                            :decimals="2"
                            decimal-separator=","
                            @change="$emit('changed')"
                        ></Numeric>
                    </TableColumn>

                    <TableColumn class="w-[8%]">
                        <Numeric
                            v-model="item.tax"
                            :unsigned="true"
                            :decimals="2"
                            decimal-separator=","
                            @change="$emit('changed')"
                        ></Numeric>
                    </TableColumn>

                    <TableColumn class="w-[10%] text-right">
                        {{ getPrice(calculated?.items?.[item.uuid]?.total ?? "--") }}
                    </TableColumn>

                    <TableColumn class="w-[10%]">
                        <Button @click="deleteItem(item)">
                            <TextWithIcon icon="trash"></TextWithIcon>
                        </Button>
                    </TableColumn>

                </TableRow>

            </template>

        </template>

    </Table>

    <div class="flex space-x-2">
        <Button @click="addItem()">{{ $t('quote.add_line') }}</Button>
        <Button @click="resetItems()">{{ $t('quote.reset') }}</Button>
    </div>

    <Table class="mt-4">

        <template #head>
            <TableHead>{{ $t('quote.shipping_table.headers.method') }}</TableHead>
            <TableHead>{{ $t('quote.shipping_table.headers.description') }}</TableHead>
            <TableHead>{{ $t('quote.shipping_table.headers.price') }}</TableHead>
            <TableHead>{{ $t('quote.shipping_table.headers.tax') }}</TableHead>
            <TableHead>{{ $t('quote.shipping_table.headers.total') }}</TableHead>
        </template>

        <template #body>
            <TableRow>
                <TableColumn class="w-[30%]">

                    <select
                        class="cm-select-input"
                        v-model="form.data.shipping_method"
                        @change="shippingMethodChanged"
                    >
                        <option :value="null">{{ $t('quote.shipping_methods.none')}}</option>
                        <option value="standard_shipping_method">{{ $t('quote.shipping_methods.standard_shipping_method')}}</option>
                        <option value="variable_shipping_method">{{ $t('quote.shipping_methods.variable_shipping_method')}}</option>
                        <option value="expedited_shipping_method">{{ $t('quote.shipping_methods.expedited_shipping_method')}}</option>
                    </select>

                </TableColumn>

                <TableColumn class="w-[40%]">
                    <Text
                        v-model="form.data.shipping_description"
                        @change="$emit('changed')"
                    ></Text>
                </TableColumn>

                <TableColumn class="w-[10%]">
                    <Numeric
                        v-model="form.data.shipping_price"
                        :unsigned="true"
                        :decimals="2"
                        decimal-separator=","
                        @change="$emit('changed')"
                    ></Numeric>
                </TableColumn>

                <TableColumn class="w-[10%]">
                    <Numeric
                        v-model="form.data.shipping_tax"
                        :unsigned="true"
                        :decimals="2"
                        decimal-separator=","
                        @change="$emit('changed')"
                    ></Numeric>
                </TableColumn>

                <TableColumn class="w-[10%] text-right">
                    {{ getPrice(calculated?.shipping ?? "--") }}
                </TableColumn>

            </TableRow>

        </template>

    </Table>


    <Table class="mt-4">

        <template #head>
            <TableHead>{{ $t('quote.total_table.headers.subtotal') }}</TableHead>
            <TableHead>{{ $t('quote.total_table.headers.tax') }}</TableHead>
            <TableHead>{{ $t('quote.total_table.headers.total') }}</TableHead>
        </template>

        <template #body>
            <TableRow>
                <TableColumn class="text-right">
                    {{ getPrice(calculated?.totals?.subtotal ?? "--") }}
                </TableColumn>

                <TableColumn class="text-right">
                    {{ getPrice(calculated?.totals?.taxes ?? "--") }}
                </TableColumn>

                <TableColumn class="text-right">
                    {{ getPrice(calculated?.totals?.total ?? "--") }}
                </TableColumn>
            </TableRow>

        </template>

    </Table>


    <div class="flex justify-between space-x-16">

        <div class="flex flex-col w-[50%]">
            <Label>{{ $t('quote.fields.additional_information')}}</Label>
            <TextArea :rows="2" v-model="form.data.additional_information" @change="$emit('changed')"></TextArea>
        </div>

        <div class="flex flex-col w-[50%]">
            <Label>{{ $t('quote.fields.text_on_pdf')}}</Label>
            <TextArea :rows="2" v-model="form.data.text_on_pdf" @change="$emit('changed')"></TextArea>
        </div>

    </div>

    <template v-if="quotePreviewError">
        <div class="cm-form-error-message">{{ $t(quotePreviewError) }}</div>
    </template>

    <div class="mt-8 flex space-x-2">
        <Button @click="$emit('save')">{{ $t('quote.save_quote') }}</Button>

        <ModalButton>

            <template #buttonContent="{ openModal }">
                <Button @click="openModal">{{ $t('quote.preview_quote') }}</Button>
            </template>

            <template #body="{ closeModal }">
                {{ $t('quote.preview_warning') }}


            </template>

            <template #footer="{ closeModal }">
                <div class="flex justify-between">
                    <Button @click="closeModal()">{{ $t('common.no')}}</Button>
                    <Button @click="previewQuote(closeModal)">{{ $t('common.yes')}}</Button>
                </div>
            </template>

        </ModalButton>

        <Button @click="$emit('returnToShop')">{{ $t('quote.return_to_shop') }}</Button>
    </div>

</template>

<script setup>

import Table from "@/components/admin/Table.vue";
import {Label} from "@jinkosoft/label";
import {Text, TextArea} from "@jinkosoft/text";
import TableColumn from "@/components/admin/TableColumn.vue";
import TableRow from "@/components/admin/TableRow.vue";
import {useFormatCurrency} from "@jinkosoft/number-format";
import {inject, watch} from "vue";
import {useGlobals, useMutation} from "@/composables/useGlobals.js";
import CustomerSelector from "@/components/admin/customer/CustomerSelector.vue";
import TableHead from "@/components/admin/TableHead.vue";

const {
    formatCurrency,
    setFormatCurrency
} = useFormatCurrency(null, "sv-SE");

const {
    router,
    t
} = useGlobals()

const settings  = inject('$settings')

const emit      = defineEmits([
    'changed',
    'save',
    'new',
    'close',
    'customerSelected',
    'returnToShop',
    'previewQuote'
])

const props     = defineProps({
    form: Object,
    currencies: Object,
    calculated: Object,
    customer: Object,
    admin: Object,
    quotePreviewError: String,
})

setFormatCurrency(props.form.data.currency)

const addItem       = () => {

    props.form.items.push({
        number: null,
        description: null,
        quantity: 0,
        unit_price: 0,
        discount: null,
        tax: null,
        show_in_pdf: true,
        added_from_product: false,
        product_variation_id: null,
        discount_type: null,
        line_type: null,
    })

    emit('changed', true)
}

const deleteItem       = (toDelete) => {

    props.form.items        = props.form.items.filter(item => item.uuid !== toDelete.uuid)

    emit('changed')
}

const resetItems        = () => {

    props.form.items        = []

    emit('changed')
}

const getPrice         = (price, defaultValue = null) => {

    if(price){

        if(price === '--'){
            return price
        }

        if(price.amount){
            return formatCurrency(price.amount)
        }else{
            return formatCurrency(price)
        }

    }else{
        if(!defaultValue){
            return formatCurrency(0)
        }

        return defaultValue
    }

    return null
}



watch(() => props.form.data.currency, (newValue) => {

    if(newValue){
        setFormatCurrency(newValue)
    }

}, { deep: true })

const previewQuote = (closeModalCallback) => {

    closeModalCallback()
    emit('previewQuote')
}

const shippingMethodChanged = (event) => {

    if(!props.form.data.shipping_description){
        props.form.data.shipping_description = t(`quote.shipping_methods.${event.target.value}`)
    }

    emit('changed')
}

</script>